import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay4() {
  const { lesson, course, accent } = findLesson("sing-and-play", "4");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/sing-and-play/SP4-Oom-Pah-Highways-Animated.mp4"
        caption={`Secondary dominant — V of V borrows tension from a sister key`}
        hint="≈ 26 sec · A7 → D7 → G"
      />
      {/* ── 1. Context ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="The big idea" title="In the Highways — faster, sharper, a secondary dominant">
        <p>
          The first three lessons gave you the Oom-Pah at a comfortable pace — wide changes,
          white keys (mostly), familiar songs. In the Highways turns up the tempo. The chord
          changes come quickly and repeat often, which means your chord-change muscle memory
          is now the real test.
        </p>
        <p>
          This lesson also introduces a new harmonic concept: the <strong>secondary dominant</strong>.
          Instead of just I–IV–V, In the Highways adds a moment where the tonic chord (D) becomes
          a 7th chord (D7) — turning briefly into the V of IV. That D7 isn't the end of a phrase;
          it's a push into the next one. It's the difference between a song that sits still and a
          song that moves forward on its own.
        </p>
        <SAPQuote>
          Do the dominant seventh of the tonic chord, so it behaves as a secondary
          dominant.
        </SAPQuote>
      </SAPSection>

      {/* ── 2. Reference recording ─────────────────────────────────────── */}
      <SAPSection eyebrow="Listen first" title="The Carter Family — the original recording">
        <p>
          The Carter Family recorded some of the most important American folk music of the early
          20th century. This is one of Maybelle Carter's arrangements — notice how the guitar
          bass line does exactly the same thing as the Oom-Pah piano pattern. Root on the beat,
          chord on the off-beat. Steady, unhurried, grounded. That's the model.
        </p>
        <YouTubeEmbed
          videoId="rbbXmvaKF9M"
          title="The Carter Family — In the Highways"
          caption="Maybelle Carter's guitar is doing Oom-Pah. Listen to the bass notes anchoring every downbeat."
        />
      </SAPSection>

      {/* ── 3. The chords + secondary dominant ────────────────────────── */}
      <SAPSection eyebrow="The harmony" title="D major with a secondary dominant — the extra push">
        <p>
          D, G, and A7 — the same three chords from I'll Fly Away. But here, the phrases are
          shorter and the changes come faster: four bars covers the full I–IV–V–I cycle. And the
          trick that makes this song more than plain folk is the <strong>D7</strong> that appears
          right before the G chord.
        </p>
        <p>
          Technically, D7 functioning as V of IV (G) is called a secondary dominant — it's the
          dominant 7th chord of the next chord in the sequence, not of the home key. You're
          temporarily borrowing the grammar of G major (where D7 would be V7) to create forward
          motion into G. It's four chords, not three — but the fourth is borrowed from a
          neighbouring key. That's what gives the song its "extra push" feeling.
        </p>

        <SAPChordRow
          label="Full vocabulary · D major with secondary dominant"
          chords={[
            { label: "D",  rn: "I",     pitches: ["D3","F#3","A3","D4"], fn: "tonic" },
            { label: "G",  rn: "IV",    pitches: ["G3","B3","D4","G4"],  fn: "subdominant" },
            { label: "A7", rn: "V7",    pitches: ["A3","C#4","E4","G4"], fn: "dominant" },
            { label: "D7", rn: "V/IV",  pitches: ["D3","F#3","A3","C4"], fn: "special" },
          ]}
          caption="D7 (V/IV) — the secondary dominant. It resolves to G (IV), not to the home key. That's the 'extra push.'"
        />

        <RomanNumeralAnalysis
          label="Verse · D major"
          caption="Fast 4-bar cycle. D7 as secondary dominant appears right before G, creating forward harmonic momentum."
          chords={[
            { chord: "D",  rn: "I",    function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
            { bar: true },
            { chord: "D7", rn: "V/IV", function: "Secondary V",  pitches: ["D3","F#3","A3","C4"] },
            { chord: "G",  rn: "IV",   function: "Pre-dominant", pitches: ["G3","B3","D4","G4"] },
            { bar: true },
            { chord: "D",  rn: "I",    function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
            { bar: true },
            { chord: "A7", rn: "V7",   function: "Dominant",     pitches: ["A3","C#4","E4","G4"] },
            { chord: "D",  rn: "I",    function: "Tonic",        pitches: ["D3","F#3","A3","D4"] },
          ]}
        />
      </SAPSection>

      {/* ── 4. Interactive loop ────────────────────────────────────────── */}
      <SAPSection eyebrow="Interactive" title="The Oom-Pah loop — D major, fast cycle">
        <p>
          This loop is shorter per chord than the previous lessons — just two or one bar per
          chord. Set the BPM to around 80 and feel how quickly the changes arrive. This is the
          pace of the real song. Your chord-change reflexes need to be fast here.
        </p>
        <OomPahPlayer
          progression={[
            { label: "D",  bars: 1, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","D5"] },
            { label: "D7", bars: 1, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","C5"] },
            { label: "G",  bars: 1, lhRoot: "G3", lhFifth: "D4", rh: ["G4","B4","D5"] },
            { label: "D",  bars: 1, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","D5"] },
            { label: "A7", bars: 1, lhRoot: "A3", lhFifth: "E4", rh: ["A4","C#5","E5"] },
            { label: "D",  bars: 1, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","D5"] },
          ]}
          bpm={82}
          label="IN THE HIGHWAYS — D major · 6-bar loop with secondary dominant"
        />
        <SAPCallout label="Hear the secondary dominant" variant="insight">
          Listen carefully to bars 1 and 2. The D7 (bar 2) sounds like it's leaning forward —
          the C natural in the chord is an unstable note in D major. It needs to go somewhere.
          And it does: it slides <em>down</em> a semitone to the B in the G chord — a seventh
          always falls. Meanwhile the F♯ underneath it pushes <em>up</em> to G. Two voices
          closing in on the new chord from opposite sides: that's the harmonic logic of the
          secondary dominant in action.
        </SAPCallout>
      </SAPSection>

      {/* ── 5. The technique — speed and muscle memory ─────────────────── */}
      <SAPSection eyebrow="Technique" title="Chord changes at speed — muscle memory over thinking">
        <p>
          At the tempo of In the Highways, you don't have time to <em>think</em> about the next
          chord. You have to have it in your hands already. This is what chord-change muscle
          memory means: the shape of the next chord is formed before the last one ends.
        </p>
        <SAPCallout label="Anticipation drill" variant="tip">
          While playing D, form the G chord shape in your right hand — without playing it. Feel
          the shape, lock it in. Then, on the change beat, it's already there. You're not
          reaching for G; you're already holding it. This "pre-forming" is the fastest way to
          clean up chord changes.
        </SAPCallout>
        <p>
          Same idea in the left hand: as you play the last Oom-Pah beat on D, move your left
          thumb to G already. The bass change happens as the left hand comes down, not as it
          lifts off. Anticipate forward.
        </p>

      </SAPSection>

      {/* ── 6. Why this matters beyond the song ───────────────────────── */}
      <SAPSection eyebrow="Music theory" title="What the secondary dominant teaches you">
        <p>
          Once you hear the secondary dominant in In the Highways, you'll start hearing it
          everywhere. Country, gospel, pop, jazz — the V/IV (dominant of the fourth) is a move
          you'll meet again and again in these styles. It's the chromatic note that
          says "wait, there's somewhere more interesting to go."
        </p>
        <SAPCallout label="Listen for it" variant="insight">
          The next time you hear a song and a chord suddenly sounds richer — more tense, more
          forward-leaning — there's a good chance it's a secondary dominant at work. The rule
          is: if a major chord appears where you'd expect a minor chord (like D major in G major
          context), it's probably V of something. Chase the resolution note to find out what.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Practice plan ───────────────────────────────────────────── */}

      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/04-in-the-highways.png"
          caption="Jason's chord chart — In the Highways in D♭ major"
        />
      </SAPSection>
      <SAPSection eyebrow="Practice plan" title="Build speed and hear the harmony">
        <SAPPracticeLadder
          intro="This lesson is a tempo challenge. Work through the steps at 70 bpm first. Add speed only after the harmony is locked in your hands."
          rungs={[
            {
              title: "Plain I–IV–V — no secondary dominant",
              minutes: 5,
              detail: "D → G → D → A7 → D, block chords. Feel the progression before adding the D7. This is the skeleton.",
              accent: "amber",
            },
            {
              title: "Insert the D7",
              minutes: 5,
              detail: "Now add D7 right before each G. D – D7 – G. Hear the difference. Sing the resolution note (B) in your head as G arrives.",
              accent: "amber",
            },
            {
              title: "Full Oom-Pah at 70 bpm",
              minutes: 8,
              detail: "Both hands, full pattern, all chord changes including D7. One bar per chord. Don't rush — accuracy first.",
              accent: "purple",
            },
            {
              title: "Speed up to 90 bpm",
              minutes: 8,
              detail: "Bring the tempo up gradually. Use the anticipation drill: pre-form the next chord shape before the change beat.",
              accent: "purple",
            },
            {
              title: "Sing the melody",
              minutes: 10,
              detail: "Add the vocal melody at 85 bpm. The song moves fast — keep the LH as the engine. Eyes up, voice forward.",
              accent: "emerald",
            },
          ]}
        />
      </SAPSection>
    </LessonShell>
  );
}
